class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) || 0;
    this.progress = this.querySelector('progress-bar');
    this.progressFill = this.querySelector('.progress-bar__fill');
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.onCartMutated = this.onCartMutated.bind(this);
    this.refreshFromCart = this.refreshFromCart.bind(this);
    this.refreshTimeout = null;
    this.observer = null;
  }

  connectedCallback() {
    this.updateShippingGoal(Number(this.dataset.cartTotal));

    const cartRoot = this.closest('#mini-cart') || document.body;
    this.observer = new MutationObserver(this.onCartMutated);
    this.observer.observe(cartRoot, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    document.addEventListener('cart:update', this.refreshFromCart);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.refreshFromCart);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.refreshTimeout) {
      window.clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  onCartMutated() {
    this.scheduleRefresh();
  }

  scheduleRefresh() {
    if (this.refreshTimeout) {
      window.clearTimeout(this.refreshTimeout);
    }
    this.refreshTimeout = window.setTimeout(this.refreshFromCart, 120);
  }

  refreshFromCart() {
    this.refreshTimeout = null;
    const cartApi = window.Shopify?.theme?.cart;
    if (cartApi?.getCart) {
      cartApi
        .getCart()
        .then((cart) => {
          const nextAmount = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
          this.dataset.cartTotal = String(nextAmount);
          this.currency = cart?.currency?.iso_code || this.currency;
          this.updateShippingGoal(nextAmount);
        })
        .catch(() => {
          this.updateShippingGoal(Number(this.dataset.cartTotal || 0));
        });
      return;
    }

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const nextAmount = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
        this.dataset.cartTotal = String(nextAmount);
        this.currency = cart?.currency || this.currency;
        this.updateShippingGoal(nextAmount);
      })
      .catch(() => {
        this.updateShippingGoal(Number(this.dataset.cartTotal || 0));
      });
  }

  /** @param {number} amount */
  updateShippingGoal(amount) {
    if (!this.goal || !(this.progress instanceof HTMLElement)) return;
    if (!(this.progressFill instanceof HTMLElement)) {
      this.progressFill = this.querySelector('.progress-bar__fill');
    }

    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (!Number.isFinite(this.percent)) this.percent = 0;
    if (this.percent >= 100) this.percent = 100;
    if (this.percent < 0) this.percent = 0;

    const percentValue = `${this.percent}%`;
    if (this.progressFill instanceof HTMLElement) {
      this.progressFill.style.width = percentValue;
    }

    if (this.cartTotal >= this.goal) {
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = String(this.cartTotal);
      this.progress.dataset.max = String(this.goal);
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      const leftToSpendElement = this.querySelector(this.selectors.leftToSpend);
      if (leftToSpendElement) {
        leftToSpendElement.innerHTML = this.formatMoney(spend);
      }
      this.classList.remove('free-shipping-goal--done');
      this.progress.dataset.value = String(this.cartTotal);
      this.progress.dataset.max = String(this.goal);
    }
  }

  /** @param {number} cents */
  formatMoney(cents) {
    const normalizedCents = Number(cents);
    if (!Number.isFinite(normalizedCents)) return '';
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(normalizedCents, this.moneyFormat);
    }
    return `${(normalizedCents / 100).toFixed(2)} ${this.currency}`;
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);
